import { TypographyVariant } from '@televet/kibble-ui/build/shared/types/typography';
import { SemanticBackgroundColorToken } from '@televet/kibble-ui/build/theme/tokens';
import {
  CareEnrollmentFragment,
  CareSubscriptionPaymentStatus,
  EnrollmentStatus,
  SidePanelOrganizationPetFragment,
  StripeDisputeStatus,
} from 'shared/types/graphql';

export const isEnrollmentCancelledButStillPaying = (
  enrollment?: Pick<CareEnrollmentFragment, 'status' | 'careSubscriptionTrueUps'>,
): boolean => {
  return (
    enrollment?.status === EnrollmentStatus.Expired &&
    !!enrollment?.careSubscriptionTrueUps.some((trueUp) => !trueUp.completedAt)
  );
};

// Considers Active or Expired with unpaid true ups as active
export const isEnrollmentConsideredActive = (
  enrollment?: Pick<CareEnrollmentFragment, 'status' | 'careSubscriptionTrueUps'>,
): boolean => {
  return enrollment?.status === EnrollmentStatus.Active || isEnrollmentCancelledButStillPaying(enrollment);
};

export type CareSubscriptionStatus = CareSubscriptionPaymentStatus | 'CancelledButStillPaying' | 'HasDisputedPayments';

export const getCareBadgeBackgroundColor = (status?: CareSubscriptionStatus | null): SemanticBackgroundColorToken => {
  switch (status) {
    case CareSubscriptionPaymentStatus.PastDue:
    case 'HasDisputedPayments':
      return 'background.danger';
    case 'CancelledButStillPaying':
      return 'background.subtle';
    default:
      return 'background.warning';
  }
};

export const getCareBadgeIconVariantColor = (status?: CareSubscriptionStatus | null): TypographyVariant => {
  switch (status) {
    case CareSubscriptionPaymentStatus.PastDue:
    case 'HasDisputedPayments':
      return 'danger';
    case 'CancelledButStillPaying':
      return 'placeholder';
    default:
      return 'warning';
  }
};

export const getSubscriptionPaymentStatus = (
  organizationPet?: SidePanelOrganizationPetFragment | null,
): CareSubscriptionStatus | null | undefined => {
  if (!organizationPet) {
    return undefined;
  }

  const enrollment = organizationPet?.carePlanEnrollments?.find((enrollment) =>
    isEnrollmentConsideredActive(enrollment),
  );

  const subscription = organizationPet?.associatedSubscriptions.find(
    (subscription) => subscription.id === enrollment?.careStripeSubscriptionId,
  );
  const isCancelledButStillPaying = isEnrollmentCancelledButStillPaying(enrollment);

  const hasDisputedPayments = enrollment?.enrollmentPayments?.some(
    (payment) =>
      payment.stripeDispute &&
      payment.stripeDispute.status !== StripeDisputeStatus.Won &&
      payment.stripeDispute.status !== StripeDisputeStatus.WarningClosed,
  );

  return isCancelledButStillPaying
    ? 'CancelledButStillPaying'
    : hasDisputedPayments
    ? 'HasDisputedPayments'
    : subscription?.paymentStatus;
};
